/**
 * Rendu « vraies perles » du patron sur canvas 2D (preview réaliste du tunnel).
 *
 * Entrée : le buffer RGBA « 1 perle = 1 pixel » produit par le worker pipeline
 * (cf. `ConvertResult.preview` dans `pipeline/worker-client.ts`). Chaque
 * cellule est dessinée comme une perle Hama midi vue de dessus : disque de la
 * couleur de la perle, trou central plus sombre, léger ombrage radial pour le
 * volume, micro-espace entre perles.
 *
 * Performance : un sprite de perle est pré-dessiné PAR COULEUR de palette
 * (≤ quelques dizaines après quantification) puis étampé d'un seul `drawImage`
 * par cellule — aucune création d'objet dans la boucle des cellules. Sous
 * `MIN_BEAD_DIAMETER` px par perle (grands patrons type 500×500), on bascule
 * en rendu simple « pixels nets » (putImageData + upscale nearest) : dessiner
 * 250 000 trous invisibles n'apporterait rien.
 *
 * NB : les ombrages utilisent des rgba() noir/blanc en alpha — ce sont des
 * masques de lumière neutres appliqués sur la couleur de CHAQUE perle, pas
 * des couleurs de marque (les CSS custom properties ne sont de toute façon
 * pas résolubles dans un contexte canvas).
 */

/** Sous ce diamètre (px), une perle n'est plus lisible → rendu « pixels nets ». */
export const MIN_BEAD_DIAMETER = 4;
/** Au-delà, inutile de grossir la perle (petits patrons sur grand canvas). */
export const MAX_BEAD_DIAMETER = 64;
/** Côté max par défaut du canvas produit. */
export const DEFAULT_MAX_CANVAS_SIDE = 1600;

const TAU = Math.PI * 2;

export type RenderBeadsOptions = {
  /** Côté max du canvas final en px (défaut : DEFAULT_MAX_CANVAS_SIDE). */
  maxCanvasSide?: number;
};

/**
 * Diamètre d'une perle en px pour que la grille tienne dans `maxCanvasSide`.
 * Retourne 0 si les dimensions sont invalides ou si le patron dépasse déjà
 * le canvas cible à 1 px/perle (le rendu retombe alors sur l'échelle 1:1).
 */
export function beadDiameterFor(
  width: number,
  height: number,
  maxCanvasSide: number = DEFAULT_MAX_CANVAS_SIDE,
): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return 0;
  }
  const side = Math.max(width, height);
  return Math.min(MAX_BEAD_DIAMETER, Math.floor(maxCanvasSide / side));
}

export type BeadGeometry = {
  /** Rayon du disque de la perle (px). */
  radius: number;
  /** Retrait du disque dans sa cellule — le micro-espace entre perles (px). */
  inset: number;
  /** Rayon du trou central (px). */
  holeRadius: number;
};

/** Géométrie d'une perle pour un diamètre de cellule donné (pure, testée à part). */
export function beadGeometry(diameter: number): BeadGeometry {
  const inset = Math.max(0.5, diameter * 0.055);
  const radius = diameter / 2 - inset;
  return { radius, inset, holeRadius: radius * 0.4 };
}

/**
 * Dessine le patron `preview` (RGBA, largeur×hauteur perles) dans `canvas`.
 *
 * - Les cellules d'alpha 0 (pas de perle) ne sont pas dessinées.
 * - Le canvas est redimensionné pour tenir dans `maxCanvasSide`.
 * - Si le diamètre calculé passe sous MIN_BEAD_DIAMETER → rendu « pixels nets ».
 *
 * Jette une erreur si le buffer ne correspond pas aux dimensions (bug appelant).
 * No-op silencieux si le contexte 2D n'est pas disponible (SSR, jsdom).
 */
export function renderBeads(
  canvas: HTMLCanvasElement,
  preview: Uint8ClampedArray,
  width: number,
  height: number,
  opts?: RenderBeadsOptions,
): void {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new Error(`Grille invalide : ${width}×${height}`);
  }
  if (preview.length !== width * height * 4) {
    throw new Error(
      `Buffer RGBA inattendu : ${preview.length} octets pour ${width}×${height} perles`,
    );
  }

  const diameter = beadDiameterFor(width, height, opts?.maxCanvasSide ?? DEFAULT_MAX_CANVAS_SIDE);
  const beadMode = diameter >= MIN_BEAD_DIAMETER;
  const scale = beadMode ? diameter : Math.max(1, diameter);

  // Dimensionné AVANT le rendu (l'affectation reset aussi le canvas).
  canvas.width = width * scale;
  canvas.height = height * scale;

  if (beadMode) {
    drawBeadCells(canvas, preview, width, height, diameter);
  } else {
    drawFlatCells(canvas, preview, width, height);
  }
}

/* ------------------------------------------------------------------------ */

/** Rendu « vraies perles » : un sprite par couleur, un drawImage par cellule. */
function drawBeadCells(
  canvas: HTMLCanvasElement,
  preview: Uint8ClampedArray,
  width: number,
  height: number,
  diameter: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const shade = makeShadeSprite(diameter);
  if (!shade) return;

  // Cache de sprites par couleur 24 bits — la palette quantifiée est petite.
  const sprites = new Map<number, HTMLCanvasElement | null>();

  for (let y = 0; y < height; y++) {
    const row = y * width;
    const py = y * diameter;
    for (let x = 0; x < width; x++) {
      const o = (row + x) * 4;
      if ((preview[o + 3] ?? 0) === 0) continue; // pas de perle ici
      const key = ((preview[o] ?? 0) << 16) | ((preview[o + 1] ?? 0) << 8) | (preview[o + 2] ?? 0);
      let sprite = sprites.get(key);
      if (sprite === undefined) {
        sprite = makeBeadSprite(diameter, key, shade);
        sprites.set(key, sprite);
      }
      if (sprite) ctx.drawImage(sprite, x * diameter, py);
    }
  }
}

/** Rendu simple « pixels nets » : putImageData natif puis upscale nearest. */
function drawFlatCells(
  canvas: HTMLCanvasElement,
  preview: Uint8ClampedArray,
  width: number,
  height: number,
): void {
  if (typeof document === 'undefined') return;
  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const offCtx = off.getContext('2d');
  if (!offCtx) return;
  // slice() : copie sur un ArrayBuffer frais (satisfait ImageData, et protège
  // d'un buffer sous-jacent partagé/détaché).
  offCtx.putImageData(new ImageData(preview.slice(), width, height), 0, 0);

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(off, 0, 0, canvas.width, canvas.height);
}

/**
 * Calque d'ombrage commun à toutes les perles d'un rendu : reflet décalé
 * haut-gauche, assombrissement du bord (volume), trou central plus sombre.
 * Dessiné UNE fois puis composé dans chaque sprite de couleur.
 */
function makeShadeSprite(diameter: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = diameter;
  c.height = diameter;
  const ctx = c.getContext('2d');
  if (!ctx) return null;

  const { radius, holeRadius } = beadGeometry(diameter);
  const cx = diameter / 2;
  const cy = diameter / 2;

  // Volume : lumière en haut-gauche → ombre vers le bord du disque.
  const light = ctx.createRadialGradient(
    cx - radius * 0.35,
    cy - radius * 0.4,
    radius * 0.1,
    cx,
    cy,
    radius,
  );
  light.addColorStop(0, 'rgba(255, 255, 255, 0.32)');
  light.addColorStop(0.55, 'rgba(255, 255, 255, 0.04)');
  light.addColorStop(0.82, 'rgba(0, 0, 0, 0.05)');
  light.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, TAU);
  ctx.fill();

  // Trou central : plus sombre vers le haut (paroi interne à l'ombre).
  const hole = ctx.createRadialGradient(
    cx,
    cy - holeRadius * 0.35,
    holeRadius * 0.15,
    cx,
    cy,
    holeRadius,
  );
  hole.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
  hole.addColorStop(0.75, 'rgba(0, 0, 0, 0.36)');
  hole.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
  ctx.fillStyle = hole;
  ctx.beginPath();
  ctx.arc(cx, cy, holeRadius, 0, TAU);
  ctx.fill();

  // Arête du trou légèrement marquée pour la lisibilité.
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.lineWidth = Math.max(0.5, diameter * 0.02);
  ctx.beginPath();
  ctx.arc(cx, cy, holeRadius, 0, TAU);
  ctx.stroke();

  return c;
}

/** Sprite complet d'une perle : disque de la couleur + calque d'ombrage. */
function makeBeadSprite(
  diameter: number,
  colorKey: number,
  shade: HTMLCanvasElement,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = diameter;
  c.height = diameter;
  const ctx = c.getContext('2d');
  if (!ctx) return null;

  const { radius } = beadGeometry(diameter);
  ctx.fillStyle = `#${colorKey.toString(16).padStart(6, '0')}`;
  ctx.beginPath();
  ctx.arc(diameter / 2, diameter / 2, radius, 0, TAU);
  ctx.fill();
  ctx.drawImage(shade, 0, 0);
  return c;
}
